import { z } from 'zod';
import { API_BASE, ApiError, apiGet } from '@/lib/api';
import { downloadBlob } from '@/lib/export';

/**
 * RAPORT SKŁADANY NA SERWERZE — katalog kolumn i pobranie pliku.
 *
 * ⚠️ KATALOG KOLUMN POCHODZI Z API I NIE MA TU DRUGIEJ LISTY. Klient rysuje wybieraczkę z tego,
 * co odda serwer, bo ten sam katalog obsłuży wysyłkę z harmonogramu — a wtedy nie ma czyjejś
 * przeglądarki, która mogłaby dołożyć swoje kolumny. Własna lista po tej stronie znaczyłaby, że
 * plik z maila różni się od tego z ekranu przy pierwszej zmianie.
 *
 * ⚠️ TO NIE ZASTĘPUJE `export.ts`. Tamten składa plik z TEGO, CO WIDAĆ na ekranie (razem z PDF-em)
 * i zostaje bez zmian. Ten moduł obsługuje raport z WYBRANYMI kolumnami — czyli dokładnie to,
 * co pojedzie mailem.
 */

export const OpisKolumnySchema = z.object({
  klucz: z.string(),
  naglowek: z.string(),
  /** Czy wchodzi do zestawu domyślnego — tego, który odpowiada kolumnom ekranu. */
  domyslna: z.boolean(),
});
export type OpisKolumny = z.infer<typeof OpisKolumnySchema>;

const KatalogSchema = z.object({
  kolumny: z.array(OpisKolumnySchema),
  domyslne: z.array(z.string()),
});
export type Katalog = z.infer<typeof KatalogSchema>;

export type NazwaRaportu = 'transactions' | 'stanAutomatow';
/**
 * ⚠️ `txt` (tab delimited) DOŁOŻONE 31.08.2026. PDF-a tu nie ma i nie będzie: składa go `pdfmake`
 * w przeglądarce, a ten raport powstaje NA SERWERZE — po to, żeby był identyczny z tym, co jedzie
 * mailem z harmonogramu.
 */
export type FormatPliku = 'csv' | 'txt' | 'xlsx';

export const katalogKolumn = (raport: NazwaRaportu): Promise<Katalog> =>
  apiGet(`/reports/kolumny?raport=${raport}`, KatalogSchema);

/**
 * Pobranie gotowego pliku z serwera.
 *
 * ⚠️ NIE `apiPost`, bo odpowiedź nie jest JSON-em — to bajty pliku. Wspólny helper próbowałby
 * je sparsować i wywrócił się na pierwszym bajcie XLSX-a.
 *
 * ⚠️ BŁĄD CZYTAMY JAKO JSON, mimo że powodzenie jest binarne. Odmowa („nieznana kolumna",
 * „raport ma 300 tys. wierszy") przychodzi normalnym ciałem błędu i musi dojść do człowieka
 * w całości — bez tego zostałby komunikat „nie udało się pobrać", z którego nic nie wynika.
 */
export async function pobierzRaport(
  raport: NazwaRaportu,
  zadanie: Record<string, unknown>,
): Promise<{ nazwa: string; wierszy: number }> {
  const sciezka = raport === 'transactions' ? '/reports/plik/wydania' : '/reports/plik/stan';
  const res = await fetch(`${API_BASE}${sciezka}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(zadanie),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: unknown } | null;
    const message = body !== null && typeof body.message === 'string' ? body.message : `Serwer odmówił (${res.status})`;
    throw new ApiError(message, res.status);
  }

  /**
   * ⚠️ NAZWĘ BIERZEMY Z NAGŁÓWKA, nie składamy jej po raz drugi w kliencie. Serwer już ją odsiał
   * do bezpiecznych znaków i wpisał do pliku, który poszedł do magazynu; druga nazwa oznaczałaby,
   * że ten sam raport nazywa się inaczej po pobraniu niż w mailu.
   */
  const dyspozycja = res.headers.get('Content-Disposition') ?? '';
  const dopasowanie = /filename="([^"]+)"/.exec(dyspozycja);
  const nazwa = dopasowanie?.[1] ?? 'raport';
  const wierszy = Number(res.headers.get('X-Raport-Wierszy') ?? '0');

  downloadBlob(await res.blob(), nazwa);
  return { nazwa, wierszy: Number.isFinite(wierszy) ? wierszy : 0 };
}
